import { zodResolver } from '@hookform/resolvers/zod'
import clsx from 'clsx'
import { useState, useMemo, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import type {
  BulkUpdateFavoriteItemsModalProps,
  BulkUpdateFavoriteItemsModalFormVO,
} from './type'

import {
  Button,
  Dialog,
  InputRadioGroup,
  FormFieldSelect,
  FormFieldNumber,
  FormInputHorizontalLayout,
} from '@/components/ui'
import { useSalesTypesMaster } from '@/stores'

import { schema, MODES, modeOptions } from './const'

export const BulkUpdateFavoriteItemsModal = ({
  isOpen,
  onClose,
  onSave,
}: BulkUpdateFavoriteItemsModalProps) => {
  const { t } = useTranslation()
  const [mode, setMode] = useState<(typeof MODES)[number]>('both')

  const { salesTypesMaster } = useSalesTypesMaster()

  const { control, formState, handleSubmit } =
    useForm<BulkUpdateFavoriteItemsModalFormVO>({
      resolver: schema && zodResolver(schema),
      defaultValues: {
        salesType: salesTypesMaster[0].salesTypeCode,
        quantity: 0,
      },
    })

  const salesTypeOptions = useMemo(
    () =>
      salesTypesMaster.map(({ salesTypeCode, salesTypeName }) => ({
        label: salesTypeName,
        value: salesTypeCode,
      })) ?? [],
    [salesTypesMaster]
  )

  const onSubmit = useCallback(
    ({ salesType, quantity }: BulkUpdateFavoriteItemsModalFormVO) => {
      switch (mode) {
        case 'both':
          onSave({ mode, salesType, quantity })
          break
        case 'salesType':
          onSave({ mode, salesType })
          break
        case 'quantity':
          onSave({ mode, quantity })
          break
      }
      setMode('both')
    },
    [mode, onSave]
  )

  return (
    <Dialog
      title={t('favorite.item.bulkUpdate')}
      isOpen={isOpen}
      onClose={onClose}
    >
      <div className={clsx('tw-mt-2 tw-px-2 tw-pb-4')}>
        <p className={clsx('tw-text-center tw-text-sm')}>
          {t('favorite.item.bulkUpdateDescription')}
        </p>

        <div
          className={clsx(
            'tw-mt-6 tw-rounded tw-bg-primary-50 tw-px-4 tw-pt-6'
          )}
        >
          <div className={clsx('tw-flex tw-justify-center')}>
            <InputRadioGroup
              name="input-mode"
              options={modeOptions}
              selected={mode}
              handleChange={(value) => setMode(value)}
              direction="row"
            />
          </div>

          <form
            id="bulk-update-favorite-items-modal-form"
            className={clsx('tw-mt-6')}
          >
            <FormInputHorizontalLayout alignHorizontal="center">
              {mode !== 'quantity' && (
                <div className={clsx('tw-w-36')}>
                  <FormFieldSelect
                    showLabel={false}
                    height="md"
                    error={formState.errors['salesType']}
                    name={'salesType'}
                    control={control}
                    options={salesTypeOptions}
                    buttonLabel={t('contact.form.placeholder.select')}
                  />
                </div>
              )}
              {mode !== 'salesType' && (
                <div className={clsx('tw-w-36')}>
                  <FormFieldNumber
                    showLabel={false}
                    height="md"
                    error={formState.errors['quantity']}
                    name={'quantity'}
                    control={control}
                    min={0}
                  />
                </div>
              )}
            </FormInputHorizontalLayout>
          </form>
        </div>

        <div
          className={clsx(
            'tw-mt-6 tw-flex tw-w-full tw-items-center tw-space-x-2'
          )}
        >
          <Button variant="normal" onClick={onClose}>
            {t('favorite.modal.close')}
          </Button>
          <Button variant="primary" onClick={handleSubmit(onSubmit)}>
            {t('favorite.modal.save')}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}
